import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { COLOR, FONT_SIZE, SIZE, fieldBorderRadius } from '../../styles';
import { useKeyActionsRegistry } from './KeyActionProvider';
import { KEY_DESCRIPTIONS } from './keyDescriptions';

export interface ShortcutHelpProps {
  /**
   * Display MacOS help keys.  If unspecified, this is auto-detected.
   */
  showMacKeys?: boolean;
}

/**
 * Renders a "Help Screen" which shows all available keyboard shortcuts.
 */
const ShortcutHelp: React.FC<ShortcutHelpProps> = (props) => {
  const { showMacKeys } = props;

  const keyboardShortcuts = useVisibleKeyboardShortcuts();

  // Update whether the shortcut help screen is visible or not.
  const actionsRegistry = useKeyActionsRegistry();
  useEffect(() => {
    actionsRegistry.shortcutHelpVisible = true;
    return () => {
      actionsRegistry.shortcutHelpVisible = false;
    };
  }, [actionsRegistry]);

  return (
    <>
      {Object.keys(keyboardShortcuts).map((groupName) => {
        const shortcuts = keyboardShortcuts[groupName];
        return (
          <KeyboardShortcutsInfoList
            className="keyboard-shortcuts-info"
            key={`shortcut-group-${groupName}`}
          >
            <KeyboardShortcutsInfoListItem className="title">
              <Header>
                <Title>{groupName}</Title>
              </Header>
            </KeyboardShortcutsInfoListItem>
            {shortcuts.map(({ info, disabled, shortcut }) => {
              const className = disabled ? 'disabled' : 'action';
              return (
                <KeyboardShortcutsInfoListItem
                  className={className}
                  key={`keyboard-action-${groupName}-${info.title}`}
                >
                  <Header>
                    <Title>{info.title}</Title>
                    <Keycodes className="keycodes">
                      <ShortcutKey shortcut={shortcut} showMacKeys={showMacKeys} />
                    </Keycodes>
                  </Header>
                </KeyboardShortcutsInfoListItem>
              );
            })}
          </KeyboardShortcutsInfoList>
        );
      })}
    </>
  );
};

/**
 * Render a shortcut key within the ShortcutHelp screen.  This renders the
 * "CTRL + ATL + X".
 */
const ShortcutKey = (props: { shortcut: string | string[]; showMacKeys?: boolean }) => {
  const { shortcut } = props;
  const shortcuts = Array.isArray(shortcut) ? shortcut : [shortcut];

  const shortcutToShow = shortcuts[0];
  const keys = shortcutToShow.split('+').map((k) => KEY_DESCRIPTIONS[k] ?? k);
  const modifiers = keys.slice(0, -1);
  const key = keys[keys.length - 1];

  return (
    <>
      {modifiers.map((modifier) => (
        <Span key={`mod-${modifier}`}>
          <Span className="key">{modifier}</Span>
          <Span className="separator">+</Span>
        </Span>
      ))}
      <Span className="key">
        {shortcutToShow.includes('shift') && key.length === 1 ? key.toUpperCase() : key}
      </Span>
    </>
  );
};

export default ShortcutHelp;

/**
 * Hook which returns the current list of shortcuts, which auto-updates
 * if the registered shortcuts change.
 */
export function useVisibleKeyboardShortcuts() {
  const actionsRegistry = useKeyActionsRegistry();
  const [shortcuts, setShortcuts] = useState(actionsRegistry.getVisisbleKeyboardShortcuts());
  useEffect(() => {
    const listener = () => {
      setShortcuts(actionsRegistry.getVisisbleKeyboardShortcuts());
    };
    actionsRegistry.on('updated', listener);
    return () => void actionsRegistry.removeListener('updated', listener);
  }, [actionsRegistry, setShortcuts]);
  return shortcuts;
}

const KeyboardShortcutsInfoList = styled.ul`
  border-top: 1px solid ${COLOR['light-gray-light']};
  margin: ${SIZE['20']} 0 0;
  padding: ${SIZE['16']} 0 0;

  &:first-child {
    border-top: none;
    margin-top: 0;
    padding-top: 0;
  }
`;

const KeyboardShortcutsInfoListItem = styled.li`
  list-style: none;
  margin: ${SIZE['4']} 0 ${SIZE['12']};
  padding: 0;

  &.disabled {
    opacity: 0.4;
  }

  &.title {
    header {
      justify-content: center;

      h2 {
        color: ${COLOR['dark-gray-light']};
        font-size: ${FONT_SIZE['20']};
        font-weight: 300;
        margin: 0;
      }
    }
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: ${FONT_SIZE['16']};
  font-weight: 500;
  margin: ${SIZE['8']} 0;
`;

const Keycodes = styled.div`
  color: ${COLOR['turquoise-dark']};
  font-size: ${FONT_SIZE['13']};
  font-weight: 400;
`;

const Span = styled.span`
  &.key {
    border: 1px solid ${COLOR['turquoise-dark']};
    border-radius: ${fieldBorderRadius};
    display: inline-block;
    padding: 1px ${SIZE['4']};
  }

  &.separator {
    color: ${COLOR['dark-gray-lighter']};
    margin: 0 ${SIZE['4']};

    &:first-child {
      display: none;
    }
  }
`;
